import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Alert, Form, Radio} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {ADD_PICK, UPDATE_PICK} from '../resolvers';
import {
  LANGUAGE_OPTIONS,
  languageLabel,
  readMutation,
  sourceLabel,
  typeLabel,
} from '../constants';
import {buildPickInput, PickFields} from './PickFields';
import {instrumentSummary} from './InstrumentSelect';
import styles from './workspace.module.scss';

/**
 * Add one pick to an existing source/type, or edit one.
 *
 * Both mutations take a complete payload rather than a patch, so the form is
 * always prefilled from the row and always submits every field — including when
 * only a translated analysis is being changed.
 */
export const SinglePickModal = ({
  form,
  recommendation,
  source,
  type,
  pick,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(pick?.id);
  const baseLanguage = pick?.language || recommendation?.language || 'EN';
  const [language, setLanguage] = useState(baseLanguage);
  const [drafts, setDrafts] = useState({});

  const [addPick, {loading: addLoading}] = useMutation(ADD_PICK, {
    errorPolicy: 'all',
  });
  const [updatePick, {loading: updateLoading}] = useMutation(UPDATE_PICK, {
    errorPolicy: 'all',
  });

  useEffect(() => {
    setLanguage(baseLanguage);
    if (!isEdit) {
      setDrafts({});
      return;
    }
    const initialDrafts = {[pick.language]: pick.analysis || ''};
    pick.multiLang?.forEach(entry => {
      initialDrafts[entry.language] = entry.analysis || '';
    });
    setDrafts(initialDrafts);
    form.setFieldsValue({
      instrumentId: pick.instrumentId || pick.instrument?.id,
      priceAsOf: pick.priceAsOf,
      priceAsOfDate: pick.priceAsOfDate ? moment(pick.priceAsOfDate) : null,
      projectedPriceBefore: pick.projectedPriceBefore,
      projectedPriceBeforeDate: pick.projectedPriceBeforeDate
        ? moment(pick.projectedPriceBeforeDate)
        : null,
      analysis: initialDrafts[pick.language] || '',
    });
  }, [pick, isEdit, baseLanguage, form]);

  /** Keep each language's analysis while switching tabs — one save per language. */
  const handleLanguageChange = nextLanguage => {
    const current = form.getFieldValue('analysis');
    setDrafts(previous => ({...previous, [language]: current}));
    setLanguage(nextLanguage);
    form.setFieldsValue({analysis: drafts[nextLanguage] || ''});
  };

  const handleSubmit = values => {
    const input = buildPickInput(values, recommendation?.exchange?.id);

    const request = isEdit
      ? updatePick({
          variables: {
            recommendationId: recommendation.id,
            pickId: pick.id,
            language,
            input,
          },
        })
      : addPick({
          variables: {
            input: {
              recommendationId: recommendation.id,
              source,
              type,
              language,
              pick: input,
            },
          },
        });

    request
      .then(result => {
        const {payload, error: failure} = readMutation(
          result,
          isEdit
            ? 'updateSingleInstrumentRecommendationPick'
            : 'addInstrumentRecommendationPick',
        );
        if (failure) return toast.error(failure);
        toast.success(payload.message);
        onSaved?.();
        onClose();
      })
      .catch(error => toast.error(error?.message));
  };

  const editingTranslation = isEdit && language !== pick?.language;

  return (
    <div className={styles.modal}>
      <h1>{isEdit ? 'Edit pick' : 'Add pick'}</h1>
      <p className={styles.modalSubtitle}>
        {sourceLabel(source)} · {typeLabel(type)} ·{' '}
        {recommendation?.exchange?.code}
      </p>

      <div className={styles.languageRow}>
        <span className={styles.fieldLabel}>
          {isEdit ? 'Editing analysis in' : 'Analysis language'}
        </span>
        <Radio.Group
          optionType="button"
          value={language}
          options={LANGUAGE_OPTIONS}
          onChange={event =>
            isEdit
              ? handleLanguageChange(event.target.value)
              : setLanguage(event.target.value)
          }
        />
      </div>

      {editingTranslation && (
        <Alert
          className={styles.alert}
          type="info"
          showIcon
          message={`The analysis is saved as the ${languageLabel(
            language,
          )} translation. Instrument, prices and dates always update the base pick, so they are submitted unchanged.`}
        />
      )}

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <PickFields
          form={form}
          exchange={recommendation?.exchange}
          seed={isEdit ? instrumentSummary(pick?.instrument) : null}
        />
        <div className={styles.formFooter}>
          {isEdit && (
            <span className={styles.mutedText}>
              Switch language and save again to update the other analysis.
            </span>
          )}
          <SubmitButton
            smallWidth
            disabled={addLoading || updateLoading}
            label={
              addLoading || updateLoading
                ? 'Saving…'
                : isEdit
                ? `Save ${languageLabel(language)} version`
                : 'Add pick'
            }
          />
        </div>
      </Form>
    </div>
  );
};

export default SinglePickModal;
